import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import ProductCategoryDistributionLimitForm from "./ProductCategoryDistributionLimitForm";
import { FormTransformers } from "@/common/utils/formTransformers";
import { useQueryClient } from "@tanstack/react-query";
import { FileEditIcon } from "lucide-react";
import { GovernmentAllocationDistributionsLimitsData } from "../../types/governmentAllocation.type";
import { CompanyContractConsumptionControlledBy } from "@/features/company/types/contract.type";
import { useUpdateGovernmentAllocationDistributionLimit } from "../../hooks/distributionsLimits/useUpdateGovernmentAllocationDistributionLimit";
import { ProductCategoryDistributionLimitValidationSchema, productCategoryDistributionsLimitControlledByCurrencySchema, productCategoryDistributionsLimitControlledByQuantitySchema } from "../../schemas/distributionsLimits.schema";
import { updateDistributionLimitsDefaultValues } from "../../utils/updateDistributionsLimitsDefaultValues";

interface UpdateDistributionLimitProps {
  governmentAllocationId?: string;
  productCategoryDistributionLimit: GovernmentAllocationDistributionsLimitsData['product_category_distribution_limits'][number];
  controlledBy?: CompanyContractConsumptionControlledBy;
}

const UpdateDistributionLimit = ({
  governmentAllocationId,
  productCategoryDistributionLimit,
  controlledBy,
}: UpdateDistributionLimitProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const isQuantity = controlledBy === 'quantity';

  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const {
    mutate: updateGovernmentAllocationDistributionLimit,
    isPending: updateGovernmentAllocationDistributionLimitIsPending,
  } = useUpdateGovernmentAllocationDistributionLimit({
    governmentAllocationId,
    productCategoryDistributionLimit,
    controlledBy,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['governmentAllocationsDistributionsLimits', governmentAllocationId] });
      queryClient.invalidateQueries({ queryKey: ['governmentAllocation', governmentAllocationId] });

      setIsOpen(false);

      setTimeout(reset, 500);
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError, setMessages })(error);
    }
  })

  const formMethods = useForm<ProductCategoryDistributionLimitValidationSchema>({
    resolver: zodResolver(
      isQuantity
        ? productCategoryDistributionsLimitControlledByQuantitySchema
        : productCategoryDistributionsLimitControlledByCurrencySchema
    ),
    defaultValues: {
      maximum_value: '',
      maximum_quantity: '',
      product_distribution_limits_attributes: [],
    },
  });

  const {
    handleSubmit,
    reset,
    setError,
  } = formMethods;

  const onSubmit: SubmitHandler<ProductCategoryDistributionLimitValidationSchema> = (data) => updateGovernmentAllocationDistributionLimit(data);

  useEffect(() => {
    reset(updateDistributionLimitsDefaultValues(productCategoryDistributionLimit));
  }, [isOpen]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={(value) => {
          setIsOpen(value);

          if (!value) {
            setTimeout(reset, 500);
          }
        }}
        Trigger={(
          <Button
            variant="link"
            operationKlassToCheckPermission="Client::Billing::GovernmentAllocations::UpdateDistributionLimits"
          >
            <FileEditIcon strokeWidth={1} />
          </Button>
        )}
        title="Limite de distribuição do empenho"
        showClose
        className="min-w-[640px] overflow-hidden"
        disabled={updateGovernmentAllocationDistributionLimitIsPending}
        body={(
          <FormProvider {...formMethods}>
            <div className="flex flex-col gap-6 text-white-800">
              <ProductCategoryDistributionLimitForm
                governmentAllocationId={governmentAllocationId}
                productCategoryId={productCategoryDistributionLimit.product_category.id}
                controlledBy={controlledBy}
              />

              <Button
                onClick={handleSubmit(onSubmit)}
                isLoading={updateGovernmentAllocationDistributionLimitIsPending}
              >
                Salvar
              </Button>
            </div>
          </FormProvider>
        )}
      />

      <ModalMessageSimple
        messages={messages}
        setMessages={setMessages}
        isError
      />
    </>
  );
}

export default UpdateDistributionLimit;
